import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Clock, Shield, ListChecks, User, ShieldAlert } from 'lucide-react';
import { api } from '../../api/client';
import { useCrud } from '../../api/hooks/useCrud';
import { useHojeReferencia, useSimClock } from '../../api/hooks/useSimClock';
import { useAuthStore } from '../../store/authStore';
import {
  normalizarLabelTurno,
  resolverFaseTurnosPorRelogio,
  combinarFaseTurnoComGrade,
  chamadaCobreSlot,
  classificarSlotChamadaGestao,
  AuditoriaChamadasPanel,
  type FaseTurnoChamada,
} from '../../components/chamada';
import '../../components/ui/datatable.css';
import type { Professor, Turma } from '../../types/models';
import { ROUTES } from '../../api/routes';

type EscopoTurno = 'Dia' | 'Manhã' | 'Tarde' | 'Noite';

interface ProfResumo {
  id: string;
  nome: string;
  fotoUrl: string | null;
  total: number;
  feitas: number;
  emCurso: number;
  perdidas: number;
  pendentes: number;
  presentes: number;
  ausentes: number;
  alunos: number;
  turmasList: string[];
  turnosList: string[];
}

type SlotStatus = 'registrada' | 'emCurso' | 'perdida' | 'pendente';

const DIAS_ENUM = ['SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA', 'SABADO'] as const;
const DIA_LABELS: Record<string, string> = {
  SEGUNDA: 'Segunda-feira',
  TERCA: 'Terça-feira',
  QUARTA: 'Quarta-feira',
  QUINTA: 'Quinta-feira',
  SEXTA: 'Sexta-feira',
  SABADO: 'Sábado',
};

const TURNOS_ESCOPO: EscopoTurno[] = ['Dia', 'Manhã', 'Tarde', 'Noite'];

const hasFotoReal = (url?: string | null) => {
  if (!url) return false;
  const l = url.toLowerCase();
  return !l.includes('i.pravatar.cc') && !l.includes('ui-avatars.com');
};

function labelTurnoDeHorario(
  h: { turma?: { turnoOperacional?: { nome?: string }; turno?: string }; turmaId?: string },
  turmas: Turma[],
): string {
  const t =
    h.turma ||
    (h.turmaId ? turmas.find((x) => x.id === h.turmaId) : undefined);
  return normalizarLabelTurno(
    (t as any)?.turnoOperacional?.nome || (t as any)?.turno || '',
  );
}

function faseLabel(fase: FaseTurnoChamada): string {
  if (fase === 'passado') return 'Feito';
  if (fase === 'agora') return 'Agora';
  return 'Futuro';
}

export function ChamadaGestaoPage() {
  const escolaId = useAuthStore((s) => s.escolaId);
  const { hojeStr, isSimulation } = useHojeReferencia();
  const { virtualEpochMs } = useSimClock({
    pollMs: isSimulation ? 2_000 : 15_000,
  });
  const [dataFiltro, setDataFiltro] = useState(hojeStr);
  const [escopo, setEscopo] = useState<EscopoTurno>('Dia');
  const [escopoInited, setEscopoInited] = useState(false);

  useEffect(() => {
    if (isSimulation && hojeStr && hojeStr !== dataFiltro) {
      setDataFiltro(hojeStr);
    }
  }, [isSimulation, hojeStr, dataFiltro]);

  const [abaPrincipal, setAbaPrincipal] = useState<'painel' | 'auditoria'>('painel');

  const { data: auditoriasPendentes = [] } = useQuery({
    queryKey: ['chamadas-auditoria-pendentes-count', escolaId],
    queryFn: async () => {
      if (!escolaId) return [];
      const res = await api.get(`/escolas/${escolaId}/chamadas/auditoria-pendentes`);
      return res.data || [];
    },
    enabled: !!escolaId,
    refetchInterval: 15_000,
  });

  const agora = isSimulation ? new Date(virtualEpochMs) : new Date();
  const horaAtual = `${String(agora.getHours()).padStart(2, '0')}:${String(agora.getMinutes()).padStart(2, '0')}`;

  const diaSemana = useMemo((): (typeof DIAS_ENUM)[number] | null => {
    const d = new Date(dataFiltro + 'T12:00:00').getDay();
    if (d === 0) return null;
    return DIAS_ENUM[d - 1];
  }, [dataFiltro]);

  const { data: professoresCrud = [] } = useCrud<Professor>('professores');
  const { data: turmas = [] } = useCrud<Turma>('turmas');

  const { data: todosHorarios = [], isLoading: loadingH } = useQuery({
    queryKey: ['horarios-gestao', escolaId, diaSemana],
    queryFn: async () => {
      const { data } = await api.get(ROUTES.horarios.porEscola(escolaId!), {
        params: { diaSemana },
      });
      return (Array.isArray(data) ? data : data?.data ?? []).filter(
        (h: any) => h.tipo === 'AULA',
      );
    },
    enabled: !!escolaId && !!diaSemana,
  });

  const { data: chamadasDia = [], isLoading: loadingC } = useQuery({
    queryKey: ['chamadas-gestao-status', escolaId, dataFiltro],
    queryFn: async () => {
      const { data } = await api.get(ROUTES.chamadas.statusDia(escolaId!), {
        params: { data: dataFiltro },
      });
      return Array.isArray(data) ? data : [];
    },
    enabled: !!escolaId,
  });

  const loading = loadingH || loadingC;

  const fotoMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of professoresCrud) {
      if (hasFotoReal((p as any).fotoUrl)) {
        m.set(p.nome?.trim().toLowerCase() || '', (p as any).fotoUrl);
      }
    }
    return m;
  }, [professoresCrud]);

  const faseTurnoPorLabel = useMemo(
    () => resolverFaseTurnosPorRelogio(horaAtual, turmas || []),
    [horaAtual, turmas],
  );

  // Default: turno vigente no relógio (uma vez).
  useEffect(() => {
    if (escopoInited || !turmas.length) return;
    const ativo = [...faseTurnoPorLabel.entries()].find(([, f]) => f === 'agora')?.[0];
    if (ativo === 'Manhã' || ativo === 'Tarde' || ativo === 'Noite') {
      setEscopo(ativo);
    }
    setEscopoInited(true);
  }, [faseTurnoPorLabel, turmas.length, escopoInited]);

  const classificarSlot = (h: any): SlotStatus => {
    const match = (chamadasDia as any[]).find((c) => chamadaCobreSlot(c, h));
    return classificarSlotChamadaGestao(h, match, horaAtual);
  };

  const horariosEscopo = useMemo(() => {
    const list = todosHorarios as any[];
    if (escopo === 'Dia') return list;
    return list.filter((h) => labelTurnoDeHorario(h, turmas) === escopo);
  }, [todosHorarios, escopo, turmas]);

  const resumo = useMemo(() => {
    const map = new Map<string, ProfResumo>();

    for (const h of horariosEscopo) {
      const pid = h.professor?.id || h.professorId;
      if (!pid) continue;
      if (!map.has(pid)) {
        const nome = h.professor?.nome || 'Professor';
        const foto =
          fotoMap.get(nome.trim().toLowerCase()) || h.professor?.fotoUrl || null;
        map.set(pid, {
          id: pid,
          nome,
          fotoUrl: hasFotoReal(foto) ? foto : null,
          total: 0,
          feitas: 0,
          emCurso: 0,
          perdidas: 0,
          pendentes: 0,
          presentes: 0,
          ausentes: 0,
          alunos: 0,
          turmasList: [],
          turnosList: [],
        });
      }
      const r = map.get(pid)!;
      r.total++;
      const tNome = h.turma?.nome;
      if (tNome && !r.turmasList.includes(tNome)) r.turmasList.push(tNome);
      const turnoLabel = labelTurnoDeHorario(h, turmas);
      if (turnoLabel && turnoLabel !== 'Sem turno' && !r.turnosList.includes(turnoLabel)) {
        r.turnosList.push(turnoLabel);
      }

      const st = classificarSlot(h);
      if (st === 'registrada') {
        r.feitas++;
        const match = (chamadasDia as any[]).find((c) => chamadaCobreSlot(c, h));
        if (match) {
          r.presentes += match.totalPresentes || 0;
          r.ausentes += match.totalAusentes || 0;
          r.alunos +=
            match.totalAlunos ||
            (match.totalPresentes || 0) + (match.totalAusentes || 0);
        }
      } else if (st === 'emCurso') r.emCurso++;
      else if (st === 'perdida') r.perdidas++;
      else r.pendentes++;
    }

    return Array.from(map.values()).sort((a, b) => {
      if (a.feitas === a.total && b.feitas !== b.total) return 1;
      if (b.feitas === b.total && a.feitas !== a.total) return -1;
      return (
        b.perdidas - a.perdidas ||
        b.pendentes - a.pendentes ||
        a.nome.localeCompare(b.nome)
      );
    });
    // classificarSlot depende de chamadasDia/horaAtual — incluídos via deps abaixo
  }, [horariosEscopo, chamadasDia, fotoMap, turmas, horaAtual]);

  const kpis = useMemo(() => {
    const previstas = resumo.reduce((s, r) => s + r.total, 0);
    const registradas = resumo.reduce((s, r) => s + r.feitas, 0);
    const emCurso = resumo.reduce((s, r) => s + r.emCurso, 0);
    const perdidas = resumo.reduce((s, r) => s + r.perdidas, 0);
    const aIniciar = resumo.reduce((s, r) => s + r.pendentes, 0);
    const encerraveis = registradas + perdidas;
    const aderencia =
      encerraveis > 0 ? Math.round((registradas / encerraveis) * 100) : null;
    const alunos = resumo.reduce((s, r) => s + r.alunos, 0);
    const presentes = resumo.reduce((s, r) => s + r.presentes, 0);
    const presenca =
      alunos > 0 ? Math.round((presentes / alunos) * 100) : null;
    return {
      previstas,
      registradas,
      emCurso,
      perdidas,
      aIniciar,
      aderencia,
      presenca,
    };
  }, [resumo]);

  /** Mini-contagens por turno (sempre sobre o dia completo). */
  const chipsTurno = useMemo(() => {
    const labels: Array<'Manhã' | 'Tarde' | 'Noite'> = ['Manhã', 'Tarde', 'Noite'];
    return labels
      .map((turno) => {
        const slots = (todosHorarios as any[]).filter(
          (h) => labelTurnoDeHorario(h, turmas) === turno,
        );
        if (!slots.length) return null;
        let feitas = 0;
        let emAula = 0;
        let perdidas = 0;
        let proximas = 0;
        const vazias = 0;
        for (const h of slots) {
          const st = classificarSlot(h);
          if (st === 'registrada') feitas++;
          else if (st === 'emCurso') emAula++;
          else if (st === 'perdida') perdidas++;
          else proximas++;
        }
        const faseRelogio = faseTurnoPorLabel.get(turno);
        const fase = combinarFaseTurnoComGrade(faseRelogio, {
          total: slots.length,
          feitas,
          emAula,
          proximas,
          perdidas,
          vazias,
        });
        return {
          turno,
          total: slots.length,
          feitas,
          perdidas,
          aIniciar: proximas,
          emCurso: emAula,
          fase,
        };
      })
      .filter(Boolean) as Array<{
      turno: 'Manhã' | 'Tarde' | 'Noite';
      total: number;
      feitas: number;
      perdidas: number;
      aIniciar: number;
      emCurso: number;
      fase: FaseTurnoChamada;
    }>;
  }, [todosHorarios, turmas, chamadasDia, horaAtual, faseTurnoPorLabel]);

  const dataLegivel = (() => {
    const [y, m, d] = dataFiltro.split('-').map(Number);
    if (!y || !m || !d) return dataFiltro;
    return new Date(y, m - 1, d, 12, 0, 0).toLocaleDateString('pt-BR');
  })();
  const diaLegivel = diaSemana ? DIA_LABELS[diaSemana] : 'Domingo';

  return (
    <div className="admin-page ac-chamada-page ac-gov-chamada-page" style={{ marginTop: -8, paddingBottom: 28 }}>
      <div className="ac-chamada-head">
        <div className="ac-chamada-head-top">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: '#4f46e5',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Shield size={18} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 className="ac-chamada-head-title">Governança de Chamadas</h1>
              <p className="ac-chamada-head-sub">
                {dataLegivel}
                {diaLegivel ? ` · ${diaLegivel}` : ''}
                {' · '}
                {resumo.length} professores · {kpis.previstas} aulas
                {escopo !== 'Dia' ? ` · turno ${escopo}` : ' · dia completo'}
                {isSimulation ? ' · simulação' : ''}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="date"
              value={dataFiltro}
              onChange={(e) => setDataFiltro(e.target.value)}
              disabled={isSimulation}
              title={isSimulation ? 'Data sincronizada com o simulador' : 'Escolher data'}
              style={{
                padding: '5px 8px',
                borderRadius: 8,
                border: '1px solid var(--border-color)',
                fontSize: '.78rem',
                background: 'var(--bg-main)',
                color: 'var(--text-main)',
                cursor: isSimulation ? 'not-allowed' : 'pointer',
                outline: 'none',
                opacity: isSimulation ? 0.85 : 1,
              }}
            />
            {isSimulation && (
              <span
                style={{
                  fontSize: '.62rem',
                  fontWeight: 700,
                  color: '#4f46e5',
                  background: '#e0e7ff',
                  padding: '3px 8px',
                  borderRadius: 999,
                }}
              >
                Sim · {horaAtual}
              </span>
            )}
          </div>
        </div>

        {/* Navegação Principal: Governança vs Auditoria Pedagógica */}
        <div style={{ display: 'flex', gap: 8, margin: '14px 0 10px', borderBottom: '1px solid var(--border-color, #e2e8f0)', paddingBottom: 8 }}>
          <button
            type="button"
            onClick={() => setAbaPrincipal('painel')}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: 'none',
              fontSize: '0.82rem',
              fontWeight: 700,
              cursor: 'pointer',
              background: abaPrincipal === 'painel' ? '#4f46e5' : 'transparent',
              color: abaPrincipal === 'painel' ? '#ffffff' : '#64748b',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Shield size={16} />
            Painel de Governança
          </button>

          <button
            type="button"
            onClick={() => setAbaPrincipal('auditoria')}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: 'none',
              fontSize: '0.82rem',
              fontWeight: 700,
              cursor: 'pointer',
              background: abaPrincipal === 'auditoria' ? '#d97706' : 'transparent',
              color: abaPrincipal === 'auditoria' ? '#ffffff' : '#64748b',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <ShieldAlert size={16} />
            Auditoria Pedagógica
            {auditoriasPendentes.length > 0 && (
              <span
                style={{
                  background: abaPrincipal === 'auditoria' ? '#ffffff' : '#fee2e2',
                  color: abaPrincipal === 'auditoria' ? '#d97706' : '#dc2626',
                  padding: '1px 7px',
                  borderRadius: 10,
                  fontSize: '0.7rem',
                  fontWeight: 800,
                }}
              >
                {auditoriasPendentes.length}
              </span>
            )}
          </button>
        </div>

        {abaPrincipal === 'painel' && (
          <>
            {/* Abas Dia | Manhã | Tarde | Noite */}
            <div className="ac-gov-turno-tabs" role="tablist" aria-label="Escopo por turno">
              {TURNOS_ESCOPO.map((t) => {
                const chip =
                  t === 'Dia'
                    ? null
                    : chipsTurno.find((c) => c.turno === t);
                const disabled = t !== 'Dia' && !chip;
                const active = escopo === t;
                return (
                  <button
                    key={t}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    disabled={disabled}
                    onClick={() => !disabled && setEscopo(t)}
                    className={`ac-gov-turno-tab${active ? ' is-active' : ''}${disabled ? ' is-disabled' : ''}`}
                  >
                    <span className="ac-gov-turno-tab-name">{t}</span>
                    {t === 'Dia' ? (
                      <span className="ac-gov-turno-tab-meta">{kpis.previstas} aulas</span>
                    ) : chip ? (
                      <>
                        <span className={`ac-chamada-turno-fase fase-${chip.fase}`}>
                          {faseLabel(chip.fase)}
                        </span>
                        <span className="ac-gov-turno-tab-meta">
                          {chip.feitas}/{chip.total}
                          {chip.perdidas > 0 ? ` · ${chip.perdidas} perd.` : ''}
                          {chip.aIniciar > 0 ? ` · ${chip.aIniciar} inic.` : ''}
                        </span>
                      </>
                    ) : (
                      <span className="ac-gov-turno-tab-meta">sem grade</span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="ac-chamada-kpis">
              <span className="ac-chamada-kpi" title="Aulas na grade do escopo">
                Previstas <strong>{kpis.previstas}</strong>
              </span>
              <span
                className="ac-chamada-kpi"
                title="CONCLUIDA ou ABERTA com alunos lançados"
              >
                Registradas{' '}
                <strong style={{ color: '#16a34a' }}>{kpis.registradas}</strong>
              </span>
              <span className="ac-chamada-kpi" title="ABERTA sem lançamento">
                Em curso{' '}
                <strong style={{ color: '#2563eb' }}>{kpis.emCurso}</strong>
              </span>
              <span className="ac-chamada-kpi" title="Ainda não passaram do horário e sem registro">
                A iniciar{' '}
                <strong style={{ color: kpis.aIniciar > 0 ? '#d97706' : '#16a34a' }}>
                  {kpis.aIniciar}
                </strong>
              </span>
              <span className="ac-chamada-kpi" title="Horário passou sem chamada registrada">
                Perdidas{' '}
                <strong style={{ color: kpis.perdidas > 0 ? '#dc2626' : '#64748b' }}>
                  {kpis.perdidas}
                </strong>
              </span>
              <span
                className="ac-chamada-kpi"
                title="Registradas ÷ (registradas + perdidas)"
              >
                Aderência{' '}
                <strong
                  style={{
                    color:
                      kpis.aderencia == null
                        ? '#64748b'
                        : kpis.aderencia < 80
                          ? '#dc2626'
                          : kpis.aderencia < 100
                            ? '#d97706'
                            : '#16a34a',
                  }}
                >
                  {kpis.aderencia == null ? '—' : `${kpis.aderencia}%`}
                </strong>
              </span>
              <span
                className="ac-chamada-kpi"
                title="Presentes ÷ alunos nas chamadas registradas do escopo"
              >
                Presença{' '}
                <strong
                  style={{
                    color:
                      kpis.presenca == null
                        ? '#64748b'
                        : kpis.presenca < 75
                          ? '#dc2626'
                          : '#16a34a',
                  }}
                >
                  {kpis.presenca == null ? '—' : `${kpis.presenca}%`}
                </strong>
              </span>
            </div>
          </>
        )}
      </div>

      {abaPrincipal === 'auditoria' ? (
        <div style={{ marginTop: 16 }}>
          <AuditoriaChamadasPanel escolaId={escolaId || undefined} />
        </div>
      ) : (
        <div
          className="ac-admin-data-card ac-gov-lista-card"
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-color, #e5e7eb)',
            borderRadius: 12,
          }}
        >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 14px',
            borderBottom: '1px solid var(--border-color, #e5e7eb)',
            background: 'var(--bg-main, #f8fafc)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ListChecks size={15} style={{ color: 'var(--text-muted)' }} />
            <span style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--text-main)' }}>
              Professores
            </span>
            <span style={{ fontSize: '.68rem', color: 'var(--text-muted)' }}>
              {escopo === 'Dia' ? 'dia completo' : `turno ${escopo}`}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {kpis.perdidas > 0 && (
              <span
                style={{
                  fontSize: '.68rem',
                  fontWeight: 600,
                  padding: '2px 7px',
                  borderRadius: 4,
                  background: '#fee2e2',
                  color: '#dc2626',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                }}
              >
                <AlertTriangle size={10} />
                {kpis.perdidas} perdida{kpis.perdidas > 1 ? 's' : ''}
              </span>
            )}
            <span style={{ fontSize: '.7rem', color: 'var(--text-muted)' }}>
              {resumo.length} professor{resumo.length !== 1 ? 'es' : ''}
            </span>
          </div>
        </div>

        <div className="ac-gov-lista-body" style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: '.82rem' }}>
              Carregando…
            </div>
          ) : !diaSemana ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: '.82rem' }}>
              Sem grade em domingo. Na simulação, escolha um dia letivo no cockpit.
            </div>
          ) : resumo.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: '.82rem' }}>
              Nenhuma aula programada neste escopo
            </div>
          ) : (
            resumo.map((r) => {
              const enc = r.feitas + r.perdidas;
              const pctAder = enc > 0 ? Math.round((r.feitas / enc) * 100) : null;
              const pctPres =
                r.alunos > 0 ? Math.round((r.presentes / r.alunos) * 100) : 0;
              const completo = r.feitas === r.total && r.total > 0;

              return (
                <div
                  key={r.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: `1px solid ${
                      completo
                        ? '#bbf7d020'
                        : r.perdidas > 0
                          ? '#fca5a520'
                          : 'var(--border-color, #e5e7eb)'
                    }`,
                    background: completo ? '#f0fdf408' : 'transparent',
                    transition: 'all .15s',
                  }}
                >
                  {hasFotoReal(r.fotoUrl) ? (
                    <img
                      src={r.fotoUrl!}
                      alt={r.nome}
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '2px solid #dbeafe',
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <span
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        flexShrink: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '.66rem',
                        fontWeight: 700,
                        color: '#1e3a8a',
                        background: '#dbeafe',
                        border: '2px solid #bfdbfe',
                      }}
                    >
                      <User size={20} />
                    </span>
                  )}

                  <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: '.88rem',
                        color: 'var(--text-main)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {r.nome}
                    </div>
                    <div
                      style={{
                        fontSize: '.66rem',
                        color: 'var(--text-muted)',
                        marginTop: 2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {r.turmasList.length > 0 ? r.turmasList.join(' · ') : '—'}
                      {escopo === 'Dia' && r.turnosList.length > 0
                        ? ` · ${r.turnosList.join('/')}`
                        : ''}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
                    <div style={{ textAlign: 'center', minWidth: 44 }}>
                      <div
                        style={{
                          fontSize: '.62rem',
                          color: 'var(--text-muted)',
                          fontWeight: 500,
                          marginBottom: 2,
                        }}
                      >
                        Aulas
                      </div>
                      <div
                        style={{
                          fontSize: '.82rem',
                          fontWeight: 700,
                          color: completo ? '#16a34a' : 'var(--text-main)',
                        }}
                      >
                        {r.feitas}
                        <span style={{ opacity: 0.4, fontWeight: 400 }}>/{r.total}</span>
                      </div>
                    </div>

                    <div style={{ textAlign: 'center', minWidth: 56 }}>
                      <div
                        style={{
                          fontSize: '.62rem',
                          color: 'var(--text-muted)',
                          fontWeight: 500,
                          marginBottom: 2,
                        }}
                      >
                        Aderência
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          justifyContent: 'center',
                        }}
                      >
                        <div
                          style={{
                            height: 5,
                            borderRadius: 3,
                            background: '#e5e7eb',
                            width: 30,
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              height: '100%',
                              borderRadius: 3,
                              width: `${pctAder ?? 0}%`,
                              background:
                                pctAder == null
                                  ? '#e5e7eb'
                                  : pctAder === 100
                                    ? '#16a34a'
                                    : pctAder >= 50
                                      ? '#d97706'
                                      : '#dc2626',
                              transition: 'width .3s',
                            }}
                          />
                        </div>
                        <span
                          style={{
                            fontSize: '.68rem',
                            fontWeight: 600,
                            color:
                              pctAder == null
                                ? '#94a3b8'
                                : pctAder === 100
                                  ? '#16a34a'
                                  : '#6b7280',
                          }}
                        >
                          {pctAder == null ? '—' : `${pctAder}%`}
                        </span>
                      </div>
                    </div>

                    {r.feitas > 0 && (
                      <div style={{ textAlign: 'center', minWidth: 56 }}>
                        <div
                          style={{
                            fontSize: '.62rem',
                            color: 'var(--text-muted)',
                            fontWeight: 500,
                            marginBottom: 2,
                          }}
                        >
                          Presença
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            justifyContent: 'center',
                          }}
                        >
                          <div
                            style={{
                              height: 5,
                              borderRadius: 3,
                              background: '#e5e7eb',
                              width: 30,
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                height: '100%',
                                borderRadius: 3,
                                width: `${pctPres}%`,
                                background:
                                  pctPres >= 85
                                    ? '#16a34a'
                                    : pctPres >= 70
                                      ? '#d97706'
                                      : '#dc2626',
                                transition: 'width .3s',
                              }}
                            />
                          </div>
                          <span
                            style={{
                              fontSize: '.68rem',
                              fontWeight: 600,
                              color:
                                pctPres >= 85
                                  ? '#16a34a'
                                  : pctPres >= 70
                                    ? '#d97706'
                                    : '#dc2626',
                            }}
                          >
                            {pctPres}%
                          </span>
                        </div>
                      </div>
                    )}

                    <div style={{ minWidth: 78, textAlign: 'right' }}>
                      {completo ? (
                        <span
                          style={{
                            fontSize: '.68rem',
                            fontWeight: 600,
                            padding: '3px 8px',
                            borderRadius: 6,
                            background: '#dcfce7',
                            color: '#166534',
                          }}
                        >
                          Completo
                        </span>
                      ) : r.perdidas > 0 ? (
                        <span
                          style={{
                            fontSize: '.68rem',
                            fontWeight: 600,
                            padding: '3px 8px',
                            borderRadius: 6,
                            background: '#fee2e2',
                            color: '#dc2626',
                          }}
                        >
                          {r.perdidas} perdida{r.perdidas > 1 ? 's' : ''}
                        </span>
                      ) : r.emCurso > 0 ? (
                        <span
                          style={{
                            fontSize: '.68rem',
                            fontWeight: 600,
                            padding: '3px 8px',
                            borderRadius: 6,
                            background: '#dbeafe',
                            color: '#1d4ed8',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 3,
                          }}
                        >
                          <Clock size={10} />
                          {r.emCurso} em curso
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: '.68rem',
                            fontWeight: 600,
                            padding: '3px 8px',
                            borderRadius: 6,
                            background: '#fef3c7',
                            color: '#92400e',
                          }}
                        >
                          {r.pendentes} pend.
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      )}
    </div>
  );
}
